import {Subscription} from "rxjs";
import {Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {FormGroup, FormsModule, ReactiveFormsModule} from "@angular/forms";

import {NgSelectComponent} from "@ng-select/ng-select";

import {
  AdDeliveryOptionApiService,
  AdDeliveryOptionStateService,
  AdPaymentOptionApiService,
  AdPaymentOptionStateService,
  MunicipalityApiService,
  PopulatedPlaceApiService,
  RegionApiService
} from "@numizmat/services/nomenclature";
import {
  AdDeliveryOptionResponse,
  AdPaymentOptionResponse,
  MunicipalityResponse,
  PopulatedPlaceResponse,
  RegionResponse
} from "@numizmat/services-models/nomenclature";
import {UserDefaultAddressApiService} from "@numizmat/services/core";
import {PrivateAdDetailResponse} from "@numizmat/services-models/core";

@Component({
  selector: 'nz-ad-form-step-03',
  imports: [
    FormsModule,
    NgSelectComponent,
    ReactiveFormsModule
  ],
  templateUrl: './ad-form-step-03.component.html',
  styleUrl: './ad-form-step-03.component.scss'
})
export class AdFormStep03Component implements OnInit, OnDestroy {
  @Input() form!: FormGroup;
  @Input() adDetails?: PrivateAdDetailResponse;
  @Input() mode!: 'create' | 'update' | 'create-similar';
  @Output() adSelectedPaymentOptionIdsChange: EventEmitter<number[]> = new EventEmitter<number[]>();
  @Output() adSelectedDeliveryOptionIdsChange: EventEmitter<number[]> = new EventEmitter<number[]>();

  private regionApiService: RegionApiService = inject(RegionApiService);
  private municipalityApiService: MunicipalityApiService = inject(MunicipalityApiService);
  private populatedPlaceApiService: PopulatedPlaceApiService = inject(PopulatedPlaceApiService);
  private userDefaultAddressApiService: UserDefaultAddressApiService = inject(UserDefaultAddressApiService);
  private adPaymentOptionApiService: AdPaymentOptionApiService = inject(AdPaymentOptionApiService);
  private adPaymentOptionStateService: AdPaymentOptionStateService = inject(AdPaymentOptionStateService);
  private adDeliveryOptionApiService: AdDeliveryOptionApiService = inject(AdDeliveryOptionApiService);
  private adDeliveryOptionStateService: AdDeliveryOptionStateService = inject(AdDeliveryOptionStateService);

  private subs: Subscription = new Subscription();

  protected regions: RegionResponse[] = [];
  protected municipalities: MunicipalityResponse[] = [];
  protected populatedPlaces: PopulatedPlaceResponse[] = [];
  protected adPaymentOptions!: AdPaymentOptionResponse[];
  protected adDeliveryOptions!: AdDeliveryOptionResponse[];

  public adSelectedPaymentOptionIds: number[] = [];
  public adSelectedDeliveryOptionIds: number[] = [];

  public ngOnInit(): void {
    this.createRegionIdValueChangesSubscription();
    this.createMunicipalityIdValueChangesSubscription();
    this.getAllRegions();
    this.getAllAdPaymentOptions();
    this.getAllAdDeliveryOptions();
    this.patchFormWithData();
  }

  public ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  protected onChangeRegionId(region: number | undefined): void {
    this.municipalityId?.setValue(null);
    this.populatedPlaceId?.setValue(null);
    this.municipalityId?.disable();
    this.populatedPlaceId?.disable();
    this.municipalities = [];
    this.populatedPlaces = [];
    if (!!region) {
      this.municipalityId?.enable();
    }
  }

  protected onChangeMunicipalityId(municipality: number | undefined): void {
    this.populatedPlaceId?.setValue(null);
    if (!!municipality) {
      this.populatedPlaceId?.enable();
    } else {
      this.populatedPlaceId?.disable();
      this.populatedPlaces = [];
    }
  }

  protected togglePaymentOption(adPaymentOptionId: number): void {
    const index = this.adSelectedPaymentOptionIds.indexOf(adPaymentOptionId);
    if (index > -1) {
      this.adSelectedPaymentOptionIds.splice(index, 1);
    } else {
      this.adSelectedPaymentOptionIds.push(adPaymentOptionId);
    }
    this.adSelectedPaymentOptionIdsChange.emit(this.adSelectedPaymentOptionIds);
  }

  protected toggleDeliveryOption(adDeliveryOptionId: number): void {
    const index = this.adSelectedDeliveryOptionIds.indexOf(adDeliveryOptionId);
    if (index > -1) {
      this.adSelectedDeliveryOptionIds.splice(index, 1);
    } else {
      this.adSelectedDeliveryOptionIds.push(adDeliveryOptionId);
    }
    this.adSelectedDeliveryOptionIdsChange.emit(this.adSelectedDeliveryOptionIds);
  }

  private patchFormWithData(): void {
    if (!!this.adDetails && this.mode !== 'create') {
      this.municipalityId?.enable();
      this.populatedPlaceId?.enable();
      this.regionId?.setValue(this.adDetails.regionId);
      this.municipalityId?.setValue(this.adDetails.municipalityId);
      this.populatedPlaceId?.setValue(this.adDetails.populatedPlaceId);
      this.additionalAdInfo?.setValue(this.adDetails.additionalAdInfo);
      this.paymentAndDeliveryInfo?.setValue(this.adDetails.paymentAndDeliveryInfo);
      this.adSelectedPaymentOptionIds = this.adDetails.adSelectedPaymentOptionIds;
      this.adSelectedDeliveryOptionIds = this.adDetails.adSelectedDeliveryOptionIds;
      this.adSelectedPaymentOptionIdsChange.emit(this.adSelectedPaymentOptionIds);
      this.adSelectedDeliveryOptionIdsChange.emit(this.adSelectedDeliveryOptionIds);
    } else {
      this.getUserDefaultAddress();
    }
  }

  private getAllRegions() {
    this.regionApiService.getAllRegions().subscribe({
      next: response => {
        this.regions = response;
      }
    });
  }

  private getMunicipalitiesByRegionId(regionId: number): void {
    this.municipalityApiService.getMunicipalitiesByRegionId(regionId).subscribe({
      next: response => {
        this.municipalities = response;
      }
    });
  }

  private getPopulatedPlacesByMunicipalityId(municipalityId: number): void {
    this.populatedPlaceApiService.getPopulatedPlacesByMunicipalityId(municipalityId).subscribe({
      next: response => {
        this.populatedPlaces = response;
      }
    });
  }

  private getUserDefaultAddress(): void {
    this.userDefaultAddressApiService.getUserDefaultAddress().subscribe({
      next: response => {
        if (response.hasAddress) {
          this.municipalityId?.enable();
          this.populatedPlaceId?.enable();
          this.regionId?.setValue(response.data.regionId);
          this.municipalityId?.setValue(response.data.municipalityId);
          this.populatedPlaceId?.setValue(response.data.populatedPlaceId);
        }
      }
    });
  }

  private getAllAdPaymentOptions(): void {
    const subscription: Subscription = this.adPaymentOptionStateService.getAllAdPaymentOptions().subscribe({
      next: adPaymentOptions => {
        if (!!adPaymentOptions) {
          this.adPaymentOptions = adPaymentOptions;
        } else {
          this.adPaymentOptionApiService.getAllAdPaymentOptions().subscribe({
            next: response => {
              this.adPaymentOptionStateService.setAllAdPaymentOptions(response);
            }
          });
        }
      }
    });
    this.subs.add(subscription);
  }

  private getAllAdDeliveryOptions(): void {
    const subscription: Subscription = this.adDeliveryOptionStateService.getAllAdDeliveryOptions().subscribe({
      next: adDeliveryOptions => {
        if (!!adDeliveryOptions) {
          this.adDeliveryOptions = adDeliveryOptions;
        } else {
          this.adDeliveryOptionApiService.getAllAdDeliveryOptions().subscribe({
            next: response => {
              this.adDeliveryOptionStateService.setAllAdDeliveryOptions(response);
            }
          });
        }
      }
    });
    this.subs.add(subscription);
  }

  private createRegionIdValueChangesSubscription(): void {
    const subscription = this.regionId?.valueChanges.subscribe({
      next: (value: number | undefined) => {
        if (!!value) {
          this.getMunicipalitiesByRegionId(value);
        }
      }
    });
    this.subs.add(subscription);
  }

  private createMunicipalityIdValueChangesSubscription(): void {
    const subscription = this.municipalityId?.valueChanges.subscribe({
      next: (value: number | undefined) => {
        if (!!value) {
          this.getPopulatedPlacesByMunicipalityId(value);
        }
      }
    });
    this.subs.add(subscription);
  }

  get regionId() {
    return this.form.get('regionId');
  }

  get municipalityId() {
    return this.form.get('municipalityId');
  }

  get populatedPlaceId() {
    return this.form.get('populatedPlaceId');
  }

  get isUserDefaultAddress() {
    return this.form.get('isUserDefaultAddress');
  }

  get additionalAdInfo() {
    return this.form.get('additionalAdInfo');
  }

  get paymentAndDeliveryInfo() {
    return this.form.get('paymentAndDeliveryInfo');
  }
}
